import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, Info, XCircle } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { StripeOnboardingModal } from '@/features/societies/components/StripeOnboardingModal';
import {
  deriveStripeBannerState,
  type StripeBannerLevel,
  type StripeBannerState,
} from '@/features/societies/helpers/stripe-banner-state';
import { useExternalStripeBanners } from '../hooks/societies/useExternalStripeBanners';

const LEVEL_STYLES: Record<
  StripeBannerLevel,
  {
    wrapper: string;
    icon: string;
    text: string;
    button: string;
    code: string;
    Icon: typeof AlertCircle;
  }
> = {
  info: {
    wrapper: 'tw-bg-yellow-50 tw-border-b tw-border-yellow-200',
    icon: 'tw-text-yellow-600',
    text: 'tw-text-yellow-800',
    button: 'tw-bg-yellow-600 hover:tw-bg-yellow-700',
    code: 'tw-text-yellow-700',
    Icon: AlertCircle,
  },
  warning: {
    wrapper: 'tw-bg-orange-50 tw-border-b tw-border-orange-200',
    icon: 'tw-text-orange-600',
    text: 'tw-text-orange-800',
    button: 'tw-bg-orange-600 hover:tw-bg-orange-700',
    code: 'tw-text-orange-700',
    Icon: Info,
  },
  danger: {
    wrapper: 'tw-bg-red-50 tw-border-b tw-border-red-200',
    icon: 'tw-text-red-600',
    text: 'tw-text-red-800',
    button: 'tw-bg-red-600 hover:tw-bg-red-700',
    code: 'tw-text-red-700',
    Icon: AlertTriangle,
  },
};

/**
 * Ligne de bandeau Stripe (présentation pure). Le `societyName`, si fourni, est
 * affiché en préfixe pour distinguer la société concernée (cas multi-société).
 * `showCodeHint` n'est vrai que pour les internals (debug/support).
 */
const StripeBannerRow: React.FC<{
  state: StripeBannerState;
  societyName?: string;
  showCodeHint: boolean;
  onCtaClick?: () => void;
}> = ({ state, societyName, showCodeHint, onCtaClick }) => {
  const styles = LEVEL_STYLES[state.level];
  const { Icon } = styles;

  // Terminal (rejected.* ou platform_disabled) → icône plus forte.
  const isTerminal =
    state.codeHint?.startsWith('rejected.') ||
    state.codeHint === 'platform_disabled';
  const DisplayIcon = isTerminal ? XCircle : Icon;

  return (
    <div className={`stripe-onboarding-banner ${styles.wrapper} tw-px-4 md:tw-px-6 tw-py-3`}>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <DisplayIcon className={`tw-w-5 tw-h-5 ${styles.icon} tw-flex-shrink-0`} />
        <div className="tw-flex-1 tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
          <p className={`tw-text-sm ${styles.text} tw-mb-0`}>
            {societyName && (
              <span className="tw-font-semibold">{societyName} — </span>
            )}
            {state.message}
          </p>
          {showCodeHint && state.codeHint && (
            <span className={`tw-text-xs tw-font-mono tw-opacity-70 ${styles.code}`}>
              [code: {state.codeHint}]
            </span>
          )}
        </div>
        {state.cta && onCtaClick && (
          <button
            onClick={onCtaClick}
            className={`tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white ${styles.button} tw-rounded-md tw-transition-colors tw-whitespace-nowrap`}
          >
            {state.cta.label}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Bandeau Stripe pour un user INTERNAL (admin Affinicar) : comportement
 * historique inchangé — un seul bandeau pour la société courante, avec le
 * `[code: ...]` de debug.
 */
const InternalStripeBanner: React.FC = () => {
  const { society: authSociety, isAuthenticated } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: effectiveConfig } = useEffectiveConfiguration(authSociety?.id);
  const hasBilledParent =
    !!effectiveConfig?.billedId && effectiveConfig.billedId !== authSociety?.id;

  const { data: society } = useSociety(authSociety?.id, {
    enabled: isAuthenticated,
  });

  if (hasBilledParent) return null;

  const bannerState = deriveStripeBannerState(
    effectiveConfig,
    society?.monthlyPayment,
  );
  if (!bannerState) return null;

  return (
    <>
      <StripeBannerRow
        state={bannerState}
        showCodeHint
        onCtaClick={bannerState.cta ? () => setIsModalOpen(true) : undefined}
      />
      {authSociety?.id && bannerState.cta && (
        <StripeOnboardingModal
          societyId={authSociety.id}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

/**
 * Bandeaux Stripe pour un user EXTERNAL (vendeur) : un bandeau par société
 * attachée au user présentant un problème Stripe.
 *
 *  - Skip des sociétés facturées par un parent (le parent porte le compte).
 *  - Dédup par `stripeAccountId` (plusieurs sociétés partageant un compte).
 *  - Préfixe du nom de société dès qu'il y a plus d'un bandeau.
 *  - Pas de `[code: ...]` (réservé aux internals).
 */
const ExternalStripeBanners: React.FC = () => {
  const [modalSocietyId, setModalSocietyId] = useState<string | null>(null);
  // Règle (permission, facturation par un parent, dédup par compte) partagée
  // avec les nouveautés, qui ne s'affichent pas tant qu'un bandeau est là.
  const { banners } = useExternalStripeBanners();

  if (banners.length === 0) return null;

  const showName = banners.length > 1;

  return (
    <>
      {banners.map(({ society, state }) => (
        <StripeBannerRow
          key={society.societyId}
          state={state}
          societyName={showName ? society.societyName : undefined}
          showCodeHint={false}
          onCtaClick={
            state.cta ? () => setModalSocietyId(society.societyId) : undefined
          }
        />
      ))}
      {modalSocietyId && (
        <StripeOnboardingModal
          societyId={modalSocietyId}
          isOpen={!!modalSocietyId}
          onClose={() => setModalSocietyId(null)}
        />
      )}
    </>
  );
};

/**
 * Bandeau Stripe affiché en haut de l'outil-pro pour signaler tout état du/des
 * compte(s) Stripe nécessitant attention (onboarding, requirements, suspension,
 * rejet, etc.). Logique d'état 100% déléguée au helper pur
 * `deriveStripeBannerState` — voir ce fichier pour la matrice des 25 cas.
 *
 * Aiguillage selon le type d'utilisateur :
 *  - INTERNAL (admin Affinicar) : un bandeau pour la société courante.
 *  - EXTERNAL (vendeur)         : un bandeau par société attachée à problème
 *    (un vendeur peut gérer plusieurs sociétés), si permission `stripe.account`.
 */
export const StripeOnboardingBanner: React.FC = () => {
  const currentUser = useAuthStore((s) => s.currentUser);

  if (currentUser?.type === 'EXTERNAL') return <ExternalStripeBanners />;
  if (currentUser?.type === 'INTERNAL') return <InternalStripeBanner />;
  return null;
};
